import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Heart, MessageCircle, Trash2, ImageIcon, Mic, MicOff, Send, Bookmark, Share2, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';

export default function FeedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState('');
  const [media, setMedia] = useState(null);
  const [posting, setPosting] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [listening, setListening] = useState(false);
  const [saved, setSaved] = useState(new Set());
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [showComposer, setShowComposer] = useState(false);
  const recognitionRef = useRef(null);
  const fileRef = useRef(null);
  const lastTapRef = useRef({});

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    try {
      const res = await api.get('/posts');
      setPosts(res.data);
    } catch { toast.error('Failed to load feed'); }
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMedia({ data: reader.result.split(',')[1], type: file.type.startsWith('video') ? 'video' : 'image' });
    reader.readAsDataURL(file);
  };

  const handlePost = async () => {
    if (!content.trim() && !media) return;
    setPosting(true);
    try {
      await api.post('/posts', { content: content.trim(), media: media?.data || null, media_type: media?.type || null });
      setContent(''); setMedia(null); setShowComposer(false);
      toast.success('Posted');
      loadPosts();
    } catch { toast.error('Failed to post'); }
    finally { setPosting(false); }
  };

  const toggleLike = async (postId) => {
    try {
      const res = await api.post(`/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.id === postId ? res.data : p));
    } catch { toast.error('Failed to like'); }
  };

  const handleDoubleTap = (postId) => {
    const now = Date.now();
    if (lastTapRef.current[postId] && now - lastTapRef.current[postId] < 300) {
      toggleLike(postId);
      lastTapRef.current[postId] = 0;
    } else {
      lastTapRef.current[postId] = now;
    }
  };

  const addComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;
    try {
      const res = await api.post(`/posts/${postId}/comment`, { content: text });
      setPosts(prev => prev.map(p => p.id === postId ? res.data : p));
      setCommentText(prev => ({ ...prev, [postId]: '' }));
    } catch { toast.error('Failed to comment'); }
  };

  const deletePost = async (postId) => {
    try {
      await api.delete(`/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const toggleSpeech = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Speech not supported'); return; }
    if (listening) { recognitionRef.current?.stop(); setListening(false); return; }
    const r = new SR();
    r.continuous = true; r.interimResults = true;
    r.onresult = (e) => { let t = ''; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; setContent(t); };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    r.start(); recognitionRef.current = r; setListening(true);
  };

  const toggleSave = (postId) => {
    setSaved(prev => { const n = new Set(prev); n.has(postId) ? n.delete(postId) : n.add(postId); return n; });
  };

  const timeAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 60) return 'now';
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  };

  return (
    <div className="max-w-xl mx-auto animate-in" data-testid="feed-page">
      {/* Stories Bar */}
      <div className="flex gap-4 mb-6 pb-4 border-b border-border/30 overflow-x-auto scrollbar-hide" data-testid="stories-bar">
        <button
          onClick={() => setShowComposer(!showComposer)}
          className="flex flex-col items-center gap-1.5 shrink-0"
          data-testid="story-create"
        >
          <div className="w-16 h-16 rounded-full border-2 border-dashed border-gold/50 flex items-center justify-center bg-gold/5 hover:bg-gold/10 transition-colors">
            <Send size={16} className="text-gold" />
          </div>
          <span className="text-[10px] text-muted-foreground">New Post</span>
        </button>
        {posts.slice(0, 8).map(p => (
          <Link to={`/profile/${p.author_id}`} key={p.id} className="flex flex-col items-center gap-1.5 shrink-0">
            <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-br from-gold via-amber-400 to-yellow-600">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <Avatar className="h-[56px] w-[56px]">
                  <AvatarFallback className="bg-obsidian text-white text-sm font-sans">{(p.author_name || 'U')[0].toUpperCase()}</AvatarFallback>
                </Avatar>
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground max-w-16 truncate">{p.author_name?.split(' ')[0]}</span>
          </Link>
        ))}
      </div>

      {/* Composer */}
      {showComposer && (
        <div className="border border-border/40 p-5 mb-6 animate-in bg-white" data-testid="create-post-form">
          <Textarea
            data-testid="post-content-input"
            placeholder="What's on your mind?"
            value={content}
            onChange={e => setContent(e.target.value)}
            className="rounded-none border-0 bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 resize-none min-h-[60px] font-sans text-sm"
            autoFocus
          />
          {media && (
            <div className="mt-3 relative inline-block">
              {media.type === 'video' ? (
                <video src={`data:video/mp4;base64,${media.data}`} className="max-h-48 rounded-sm" controls />
              ) : (
                <img src={`data:image/jpeg;base64,${media.data}`} alt="preview" className="max-h-48 rounded-sm object-cover" />
              )}
              <button onClick={() => setMedia(null)} className="absolute -top-2 -right-2 bg-obsidian text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">&times;</button>
            </div>
          )}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/20">
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleMediaSelect} />
              <Button data-testid="post-add-media-btn" variant="ghost" size="sm" className="rounded-full h-8 px-3 text-xs" onClick={() => fileRef.current?.click()}>
                <ImageIcon size={14} className="mr-1" /> Media
              </Button>
              <Button data-testid="post-speech-btn" variant="ghost" size="sm" className={`rounded-full h-8 px-3 text-xs ${listening ? 'text-gold' : ''}`} onClick={toggleSpeech}>
                {listening ? <MicOff size={14} className="mr-1" /> : <Mic size={14} className="mr-1" />}
                {listening ? 'Stop' : 'Voice'}
              </Button>
            </div>
            <Button data-testid="post-submit-btn" onClick={handlePost} disabled={posting || (!content.trim() && !media)}
              className="rounded-full text-xs h-8 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300">
              {posting ? 'Posting...' : 'Share'}
            </Button>
          </div>
        </div>
      )}

      {/* Posts */}
      <div className="space-y-6">
        {posts.length === 0 && (
          <p className="text-center text-muted-foreground text-sm py-16">No posts yet. Be the first to share.</p>
        )}
        {posts.map((post, i) => (
          <article
            key={post.id}
            className="border border-border/30 bg-white hover:shadow-sm transition-all duration-300 animate-in"
            style={{ animationDelay: `${i * 40}ms` }}
            data-testid={`post-${post.id}`}
          >
            {/* Post Header */}
            <div className="flex items-center justify-between px-4 py-3">
              <Link to={`/profile/${post.author_id}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <div className="p-[2px] rounded-full bg-gradient-to-br from-gold/60 to-amber-300/60">
                  <Avatar className="h-8 w-8 border-2 border-white">
                    <AvatarFallback className="bg-obsidian text-white text-xs font-sans">{(post.author_name || 'U')[0].toUpperCase()}</AvatarFallback>
                  </Avatar>
                </div>
                <div>
                  <p className="text-sm font-semibold leading-none">{post.author_name}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(post.created_at)}</p>
                </div>
              </Link>
              <div className="flex items-center gap-1">
                {(post.author_id === user?.id || user?.role === 'admin') && (
                  <button data-testid={`delete-post-${post.id}`} onClick={() => deletePost(post.id)} className="p-1.5 text-muted-foreground/40 hover:text-destructive transition-colors">
                    <Trash2 size={14} />
                  </button>
                )}
                <button className="p-1.5 text-muted-foreground/40 hover:text-foreground transition-colors">
                  <MoreHorizontal size={16} />
                </button>
              </div>
            </div>

            {/* Post Media */}
            {post.media && (
              <div className="relative cursor-pointer" onClick={() => handleDoubleTap(post.id)}>
                {post.media_type === 'image' ? (
                  <img src={`data:image/jpeg;base64,${post.media}`} alt="" className="w-full max-h-[500px] object-cover" />
                ) : (
                  <video src={`data:video/mp4;base64,${post.media}`} controls className="w-full max-h-[500px]" />
                )}
              </div>
            )}

            {/* Post Actions */}
            <div className="px-4 pt-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button data-testid={`like-post-${post.id}`} onClick={() => toggleLike(post.id)} className="flex items-center gap-1.5 group">
                  <Heart size={22} className={`transition-all duration-200 ${post.likes?.includes(user?.id) ? 'fill-red-500 text-red-500 scale-110' : 'text-foreground group-hover:text-red-400'}`} />
                </button>
                <button onClick={() => setExpandedComments(prev => { const n = new Set(prev); n.has(post.id) ? n.delete(post.id) : n.add(post.id); return n; })} className="group">
                  <MessageCircle size={22} className="text-foreground group-hover:text-gold transition-colors" />
                </button>
                <button className="group">
                  <Share2 size={20} className="text-foreground group-hover:text-gold transition-colors" />
                </button>
              </div>
              <button data-testid={`save-post-${post.id}`} onClick={() => toggleSave(post.id)}>
                <Bookmark size={22} className={`transition-all duration-200 ${saved.has(post.id) ? 'fill-obsidian text-obsidian' : 'text-foreground hover:text-gold'}`} />
              </button>
            </div>

            {/* Likes Count */}
            <div className="px-4 pt-2">
              <p className="text-sm font-semibold">{post.likes?.length || 0} likes</p>
            </div>

            {/* Post Content */}
            {post.content && (
              <div className="px-4 pt-1 pb-1">
                <p className="text-sm"><span className="font-semibold mr-1.5">{post.author_name}</span>{post.content}</p>
              </div>
            )}

            {/* Comments Preview */}
            {post.comments?.length > 0 && !expandedComments.has(post.id) && (
              <button onClick={() => setExpandedComments(prev => { const n = new Set(prev); n.add(post.id); return n; })} className="px-4 pt-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                View {post.comments.length > 1 ? `all ${post.comments.length} comments` : '1 comment'}
              </button>
            )}

            {/* Expanded Comments */}
            {expandedComments.has(post.id) && post.comments?.length > 0 && (
              <div className="px-4 pt-1 space-y-1">
                {post.comments.map(c => (
                  <p key={c.id} className="text-sm"><span className="font-semibold mr-1.5">{c.author_name}</span><span className="text-foreground/80">{c.content}</span></p>
                ))}
              </div>
            )}

            {/* Comment Input */}
            <div className="px-4 py-3 flex items-center gap-2 border-t border-border/10 mt-2">
              <input
                data-testid={`comment-input-${post.id}`}
                placeholder="Add a comment..."
                value={commentText[post.id] || ''}
                onChange={e => setCommentText(p => ({ ...p, [post.id]: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addComment(post.id)}
                className="flex-1 text-sm bg-transparent py-1 focus:outline-none placeholder:text-muted-foreground/40"
              />
              {commentText[post.id]?.trim() && (
                <button data-testid={`comment-submit-${post.id}`} onClick={() => addComment(post.id)} className="text-sm text-gold hover:text-amber-600 font-semibold transition-colors">
                  Post
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
